import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetch } from '../../store/openApiFetch';
import { useOpenApiFetch } from '../../store/useOpenApiFetch';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import { Modal } from '../common/Modal/Modal';
import {
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelSection,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    PanelWithoutForm,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
} from '../elements';
import { PanelSpinner } from '../spinners';
import { ObjectSelect } from '../utils/authorization/ObjectSelect';
import { useCanUserEditObject } from '../utils/authorization/useCanUserEditObject';

export function ObjectDetailsRelations(props: { objectId: string }) {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { canUserEditObject } = useCanUserEditObject();
    const [isAddingParent, setIsAddingParent] = useState(false);
    const [parentObjectIdToAdd, setParentObjectIdToAdd] = useState<string | undefined>();
    const [isAddingChild, setIsAddingChild] = useState(false);
    const [childObjectIdToAdd, setChildObjectIdToAdd] = useState<string | undefined>();

    const { data: parentObjects, reload: reloadParentObjects } = useOpenApiFetch('/api/objects/{objectId}/relations/parent', 'get', {
        objectId: props.objectId,
    });

    const { data: childObjects, reload: reloadChildObjects } = useOpenApiFetch('/api/objects/{objectId}/relations/child', 'get', {
        objectId: props.objectId,
    });

    async function addChildRelation() {
        setIsAddingChild(false);
        if (childObjectIdToAdd) {
            await openApiFetch('/api/objects/{objectId}/relations/parent', 'post', { objectId: childObjectIdToAdd }, { parentId: props.objectId });
            await reloadChildObjects();
            setChildObjectIdToAdd(undefined);
        }
    }

    async function removeChildRelation(childObjectId: string) {
        await openApiFetch('/api/objects/{objectId}/relations/parent', 'delete', { objectId: childObjectId }, { parentId: props.objectId });
        await reloadChildObjects();
    }

    async function addParentRelation() {
        setIsAddingParent(false);
        if (parentObjectIdToAdd) {
            await openApiFetch('/api/objects/{objectId}/relations/child', 'post', { objectId: parentObjectIdToAdd }, { childId: props.objectId });
            await reloadParentObjects();
            setParentObjectIdToAdd(undefined);
        }
    }

    async function removeParentRelation(parentObjectId: string) {
        await openApiFetch('/api/objects/{objectId}/relations/child', 'delete', { objectId: parentObjectId }, { childId: props.objectId });
        await reloadParentObjects();
    }

    if (!parentObjects || !childObjects) {
        return <PanelSpinner />;
    }

    return (
        <>
            <PanelWithoutForm>
                <PanelHeading text={translate('objectRelation.objectRelations')} icon="fa fa-link"></PanelHeading>
                <PanelBody id="objectRelations" noPadding={true} whiteBackground={true}>
                    <Table hover>
                        <TableHead>
                            <TableHeadRow light>
                                <TableHeader>{translate('common.name')}</TableHeader>
                                <TableHeader>{translate('common.location')}</TableHeader>
                                <TableHeader>Type</TableHeader>
                                <TableHeader>&nbsp;</TableHeader>
                            </TableHeadRow>
                        </TableHead>
                        <TableBody>
                            {parentObjects.items.map((item) => (
                                <TableRow key={item.id} onClick={() => navigate(`/objects/${item.id}`)}>
                                    <TableData>{item.name}</TableData>
                                    <TableData>{item.location}</TableData>
                                    <TableData>{translate('objectRelation.parent')} </TableData>
                                    <TableData>
                                        {canUserEditObject && (
                                            <IconRemoveButton
                                                removeQuestion={translate('common.confirmDelete', item.name)}
                                                onClick={async () => removeParentRelation(item.id)}
                                            />
                                        )}
                                    </TableData>
                                </TableRow>
                            ))}
                            {childObjects.items.map((item) => (
                                <TableRow key={item.id} onClick={() => navigate(`/objects/${item.id}`)}>
                                    <TableData>{item.name}</TableData>
                                    <TableData>{item.location}</TableData>
                                    <TableData>{translate('objectRelation.child')} </TableData>
                                    <TableData>
                                        {canUserEditObject && (
                                            <IconRemoveButton
                                                removeQuestion={translate('common.confirmDelete', item.name)}
                                                onClick={async () => removeChildRelation(item.id)}
                                            />
                                        )}
                                    </TableData>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </PanelBody>
                {canUserEditObject && (
                    <EditPanelButtons footer>
                        <PanelButtonLink
                            small
                            icon="fa fa-plus"
                            onClick={() => setIsAddingChild(true)}
                            text={translate('common.addItem', translate('objectRelation.childOfRelations'))}
                        />
                        <PanelButtonLink
                            small
                            icon="fa fa-plus"
                            onClick={() => setIsAddingParent(true)}
                            text={translate('common.addItem', translate('objectRelation.parentOfRelations'))}
                        />
                    </EditPanelButtons>
                )}
            </PanelWithoutForm>

            {isAddingChild && (
                <Modal id="dialog" onClose={() => setIsAddingChild(false)}>
                    <Modal.Title>
                        <h3>
                            <span className="panel-title">{translate('common.addItem', translate('objectRelation.childOfRelations'))} </span>
                        </h3>
                    </Modal.Title>
                    <Modal.Content style={{ height: '160px' }}>
                        <div className="admin-form" style={{ width: '100%' }}>
                            <EditPanel>
                                <EditPanelSection>
                                    <ObjectSelect
                                        objectIdsToIgnore={[props.objectId, ...childObjects.items.map(({ id }) => id)]}
                                        onChange={(value) => setChildObjectIdToAdd(value)}
                                        key={isAddingChild.toString()} // This resets the selection for when the form is re-opened
                                        onSubmit={addChildRelation}
                                    />
                                </EditPanelSection>
                            </EditPanel>
                        </div>
                    </Modal.Content>
                    <Modal.Actions>
                        <EditPanelButtons fixWidth={true}>
                            <EditPanelButton small={true} text={translate('common.cancel')} onClick={() => setIsAddingChild(false)} />
                            <EditPanelButton small={true} text={translate('common.save')} onClick={addChildRelation} primary />
                        </EditPanelButtons>
                    </Modal.Actions>
                </Modal>
            )}

            {isAddingParent && (
                <Modal id="dialog" onClose={() => setIsAddingChild(false)}>
                    <Modal.Title>
                        <h3>
                            <span className="panel-title">{translate('common.addItem', translate('objectRelation.parentOfRelations'))} </span>
                        </h3>
                    </Modal.Title>
                    <Modal.Content style={{ height: '160px' }}>
                        <div className="admin-form" style={{ width: '100%' }}>
                            <EditPanel>
                                <EditPanelSection>
                                    <ObjectSelect
                                        objectIdsToIgnore={[props.objectId, ...parentObjects.items.map(({ id }) => id)]}
                                        onChange={(value) => setParentObjectIdToAdd(value)}
                                        key={isAddingParent.toString()} // This resets the selection for when the form is re-opened
                                        onSubmit={addParentRelation}
                                    />
                                </EditPanelSection>
                            </EditPanel>
                        </div>
                    </Modal.Content>
                    <Modal.Actions>
                        <EditPanelButtons fixWidth={true}>
                            <EditPanelButton small={true} text={translate('common.cancel')} onClick={() => setIsAddingParent(false)} />
                            <EditPanelButton small={true} text={translate('common.save')} onClick={addParentRelation} primary />
                        </EditPanelButtons>
                    </Modal.Actions>
                </Modal>
            )}
        </>
    );
}
